import { useCallback, useEffect, useMemo, useState } from 'react';
import { erApiFeil } from '../api/feil.ts';
import { useMe } from '../api/useMe.ts';
import { tilgangFra } from '../skall/tilgang.ts';
import { useInnstillinger, useOppdaterInnstillinger, type CorrelationKindCreate, type ReasonCode, type ReplyTemplateCreate, type TenantSettings, type TextMaxLength } from '../api/useSettings.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Kartknapp } from '../innboks/Kartknapp.tsx';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Bryter } from '../kjentefeil/Bryter.tsx';
import { Om } from '../skall/Om.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { AVVISNINGSKODER, KATEGORI, KATEGORIER, VALGBARE, statusEtikett, statusOppforing } from '../status/status.ts';
import { Fot } from './Fot.tsx';
import { ProdukterOgNoekler } from './Noekler.tsx';
import { Rad, RadProvider, Seksjon, TekniskeBryter, useRadstyring } from './Rad.tsx';
import { KUNDEOPPSLAG_MAKS, kundeoppslagGrunn, kundeoppslagVert } from './kundeoppslag.ts';
import { useUtkast, type Utkast } from './utkast.tsx';

/** Plattformens tak — leietakeren beveger seg under, aldri over (domain-model.md). */
export const FRIST_TAK = 90;
export const KONTAKTER_TAK = 3;
export const STILLHET_UKER = 4;
export const MINORS_TEGN = 140;

/**
 * Innstillinger — rommet Innspills side (R5.3; kravlisten er `design/wireframes/admin-
 * innstillinger.jsx`, utseendet innstillinger-konsept v0.1). Det som styrer hvordan
 * sluttbrukerne sender inn, og hvordan dere svarer dem.
 *
 * Hver innstilling er ÉN rad: navnet, verdien i én lesbar linje, og «Endre» som åpner
 * feltene på stedet — eller «Vis» der innholdet har egen lagring (produkter, nøkler) eller
 * bare er visning (statusnavnene). Ingen innholdsfortegnelse, ingen trekkspill.
 *
 * Endringer samles i sidens eget utkast og sendes med «Lagre» som ÉN PATCH — utelatt felt
 * betyr «ikke rør». Kun eier kan lagre; alle roller ser (GET /settings er alle roller), og
 * for behandler og leser finnes verken «Endre» eller foten.
 *
 * Det som gjelder LEIETAKEREN og ikke rommet — navnet, personvernerklæringen, retensjonen,
 * brukergruppen, innsendernes innlogging, panelbrukere, API-nøkler og varslene ut — bor på
 * Organisasjon. Her er bare rommets.
 */
/**
 * Hvilken seksjon eier hvilket utkastfelt PÅ DENNE SIDEN. Kartet finnes for at en rad skal
 * kunne merkes «endret», og for at ingen felt skal falle mellom de to sidene.
 *
 * Vakt 9 i `test/lint-vakter.test.ts` leser skrivekallene ut av DENNE filen og av
 * `organisasjon/OrganisasjonSide.tsx`, og krever at hvert eneste felt står i nøyaktig én
 * seksjon på nøyaktig én side. Et nytt felt uten seksjon er en rød test, ikke et stille hull.
 *
 * `statusnavn` står ikke her: den er visning i v1 og har ingen felter. Produkter, SDK-nøkler
 * og «Bruk» heller ikke — de lagrer hver for seg mot serveren og er ikke del av utkastet.
 */
export const FELT_I_SEKSJON: Readonly<Record<string, readonly string[]>> = {
  // M4.10: kundeoppslaget er en lenkemal som ender på saken, som koblingsnøklenes — derfor her.
  innsending: ['textMaxLength', 'minTextLength', 'screenshotsEnabled', 'correlationKinds', 'customerLookupUrlTemplate'],
  svar: ['reviewDeadlineDays', 'replyTemplates'],
  betaprogram: ['maxContactsPerProgram'],
  community: ['communityEnabled'],
};

/**
 * Feltene hver RAD eier, i sidens rekkefølge. Radens «endret»-merke og fotens «hva lagres»
 * leser begge denne — navnet i foten er radens, aldri kontraktens feltnavn (DD-50).
 */
const FELT_I_RAD: ReadonlyArray<{ rad: string; navn: string; felter: ReadonlyArray<keyof Utkast> }> = [
  { rad: 'tekstgrense', navn: 'Tekstgrense', felter: ['textMaxLength', 'minTextLength'] },
  { rad: 'skjermbilde', navn: 'Skjermbilde', felter: ['screenshotsEnabled'] },
  { rad: 'koblingsnokler', navn: 'Koblingsnøkler', felter: ['correlationKinds'] },
  { rad: 'kundeoppslag', navn: 'Kundeoppslag', felter: ['customerLookupUrlTemplate'] },
  { rad: 'svarfrist', navn: 'Svarfrist', felter: ['reviewDeadlineDays'] },
  { rad: 'svarmaler', navn: 'Svarmaler', felter: ['replyTemplates'] },
  { rad: 'kontakter', navn: 'Maks kontakter', felter: ['maxContactsPerProgram'] },
  { rad: 'community', navn: 'Community og stemming', felter: ['communityEnabled'] },
];

/** Statusnavnene i verdilinjen: feilsporet har åtte, ønskesporet sju (ingen «Kjent feil»). */
const ORDEN_FEIL = [...VALGBARE, 'kjent_feil' as const, 'duplikat' as const];
const ORDEN_ONSKE = [...VALGBARE, 'duplikat' as const];

/** Innstillinger har én egen tast. Den står her fordi et kart uten den ville sett tomt ut. */
const TASTER: Tast[] = [['Ctrl+S', 'lagre endringer']];

export function InnstillingerSide() {
  const me = useMe();
  // Det globale laget fra `access`-modellen (M4.8, `skall/tilgang.ts`).
  const erEier = tilgangFra(me.data).eier;
  const innstillinger = useInnstillinger();
  const oppdater = useOppdaterInnstillinger();
  // Utkastet ligger over ruten (F32), med én nøkkel per side (P3).
  const { utkast, sett, tøm, antall: endringer } = useUtkast('innstillinger');
  const [toast, setToast] = useState<ToastInnhold | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const side = useRadstyring(erEier);
  // M4.10: `aria-invalid` først etter blur eller lagreforsøk (validering etter interaksjon) —
  // til da bærer fotens grunn kravet.
  const [kundeBeroert, setKundeBeroert] = useState(false);

  const s = innstillinger.data;
  const verdi = useCallback(<K extends keyof TenantSettings & keyof Utkast>(felt: K): TenantSettings[K] => (utkast[felt] !== undefined ? (utkast[felt] as TenantSettings[K]) : (s![felt] as TenantSettings[K])), [utkast, s]);

  /** Har denne raden ulagrede endringer? Leser utkastet, ikke en egen tilstand. */
  const endret = useCallback((rad: string) => (FELT_I_RAD.find((r) => r.rad === rad)?.felter ?? []).some((f) => utkast[f] !== undefined), [utkast]);
  const endretNavn = useMemo(() => FELT_I_RAD.filter((r) => r.felter.some((f) => utkast[f] !== undefined)).map((r) => r.navn), [utkast]);

  // M4.10: lenkemalen i utkastet (null = fjernes) eller den lagrede; panelets egen regel sperrer
  // foten FØR tjeneren, så 400-meldingen aldri er første møte med den (DD-50).
  const kundeMal = utkast.customerLookupUrlTemplate !== undefined ? (utkast.customerLookupUrlTemplate ?? '') : (s?.customerLookupUrlTemplate ?? '');
  const kundeGrunn = useMemo(() => kundeoppslagGrunn(kundeMal), [kundeMal]);
  const sperret = utkast.customerLookupUrlTemplate !== undefined ? kundeGrunn : null;

  const lagre = useCallback(() => {
    if (!erEier || oppdater.isPending || endringer === 0) return;
    if (sperret) {
      setKundeBeroert(true);
      return;
    }
    setFeil(null);
    oppdater
      .mutateAsync(utkast)
      .then(() => {
        tøm();
        setToast({ tekst: `Innstillingene er lagret — ${endringer} ${endringer === 1 ? 'felt' : 'felter'} endret.` });
      })
      .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Kunne ikke lagre. Prøv igjen.'));
  }, [erEier, oppdater, utkast, endringer, tøm, sperret]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        lagre();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lagre]);

  const minors = s ? verdi('minorsPresent') : false;
  const tegngrense: TextMaxLength = s ? (minors ? MINORS_TEGN : verdi('textMaxLength')) : MINORS_TEGN;
  const mangler = useMemo(() => (s ? s.communityChecklist.filter((k) => !k.ok).length : 0), [s]);
  const communityLaas = minors ? null : mangler ? `${mangler} krav gjenstår` : null;

  if (innstillinger.isPending || me.isPending) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Innstillinger</h1>
        </header>
        <div role="status" aria-busy="true" aria-label="Laster innstillinger" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
          <span className="panel-skjelett-linje panel-skjelett-kort" />
        </div>
      </>
    );
  }

  if (innstillinger.isError || !s) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Innstillinger</h1>
          <Kartknapp tittel="Innstillinger" taster={TASTER} />
        </header>
        <div role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> Kunne ikke laste innstillingene. {innstillinger.error && erApiFeil(innstillinger.error) ? innstillinger.error.brukertekst : ''}
          <button type="button" className="panel-knapp-liten" onClick={() => void innstillinger.refetch()}>
            Prøv igjen
          </button>
        </div>
      </>
    );
  }

  const maler: ReplyTemplateCreate[] = utkast.replyTemplates ?? s.replyTemplates.map((t) => ({ name: t.name, text: t.text, ...(t.reasonCode ? { reasonCode: t.reasonCode } : {}) }));
  const kinds: CorrelationKindCreate[] = utkast.correlationKinds ?? s.correlationKinds.filter((k) => !k.locked).map((k) => ({ kind: k.kind, pattern: k.pattern, ...(k.linkTemplate ? { linkTemplate: k.linkTemplate } : {}) }));
  const crash = s.correlationKinds.find((k) => k.locked);
  const alleTyper = [...(crash ? [crash.kind] : []), ...kinds.map((k) => k.kind).filter(Boolean)];
  const frist = verdi('reviewDeadlineDays');
  const minTegn = verdi('minTextLength');
  const skjermbilde = minors ? false : verdi('screenshotsEnabled');
  const oppfylt = s.communityChecklist.length - mangler;

  return (
    <>
      <header className="panel-topp">
        <h1 className="panel-h1">Innstillinger</h1>
        <Kartknapp tittel="Innstillinger" taster={TASTER} />
      </header>

      <div className="panel-innst">
        <div className="panel-innst-hode">
          <p className="panel-innst-lead">Det som styrer hvordan sluttbrukerne sender inn, og hvordan dere svarer dem.</p>
          <TekniskeBryter paa={side.tekniske} onChange={side.setTekniske} />
        </div>

        <RadProvider value={side}>
          <Seksjon id="innsending" tittel="Innsending">
            <Rad
              id="tekstgrense"
              navn="Tekstgrense"
              kode="textMaxLength · minTextLength"
              handling="Endre"
              endret={endret('tekstgrense')}
              verdi={
                <>
                  <strong>{tegngrense} tegn</strong>
                  {minors ? (
                    <span className="panel-innst-laast">
                      <Ikon navn="lock-closed-outline" /> låst
                    </span>
                  ) : null}{' '}
                  · minst {minTegn} for å sende
                </>
              }
              verdiSmal={`${tegngrense} tegn · minst ${minTegn}`}
            >
              <div className="panel-felt">
                <span id="tegngrense-l">Tekstgrense for innspillstekst</span>
                <div role="radiogroup" aria-labelledby="tegngrense-l" aria-disabled={minors || undefined} className="panel-segment">
                  {([140, 280] as TextMaxLength[]).map((v) => (
                    <button key={v} type="button" role="radio" aria-checked={tegngrense === v} aria-disabled={minors || undefined} className={`panel-segment-valg${tegngrense === v ? ' panel-segment-valgt' : ''}`} onClick={minors ? undefined : () => sett('textMaxLength', v)} data-testid={`tegngrense-${v}`}>
                      {tegngrense === v ? <Ikon navn="checkmark-outline" /> : null}
                      {v} tegn
                    </button>
                  ))}
                </div>
                {minors ? (
                  // K6, K18: bare valget 140/280 låses av minorsPresent — minste antall tegn står.
                  <span className="panel-grunn" role="status" data-testid="tegngrense-laas">
                    <Ikon navn="lock-closed-outline" /> Låst til 140 tegn fordi brukerne kan være mindreårige.
                  </span>
                ) : null}
              </div>
              {/* DD-44: minste antall tegn, ikke obligatoriske felter. Standard 10. */}
              <label className="panel-felt">
                <span>Send-knappen er aktiv fra</span>
                <input type="number" min={1} max={tegngrense} value={minTegn} onChange={(e) => sett('minTextLength', Number(e.target.value))} data-testid="minste-tegn" />
              </label>
            </Rad>

            <Rad id="skjermbilde" navn="Skjermbilde" kode="screenshotsEnabled" handling="Endre" laast={minors} endret={endret('skjermbilde')} verdi={skjermbilde ? 'På' : 'Av'}>
              <Bryter label="Innsender kan legge ved skjermbilde" ikon="image-outline" paa={skjermbilde} onChange={(v) => sett('screenshotsEnabled', v)} testid="skjermbilde" />
              <p className="panel-tekst-liten">Innsenderen velger selv, per innsending.</p>
            </Rad>

            <Rad
              id="koblingsnokler"
              navn="Koblingsnøkler"
              kode="correlationKinds"
              handling="Endre"
              endret={endret('koblingsnokler')}
              verdi={
                <>
                  <strong>
                    {alleTyper.length} {alleTyper.length === 1 ? 'type' : 'typer'}
                  </strong>{' '}
                  · {alleTyper.join(', ')}
                </>
              }
              verdiSmal={`${alleTyper.length} ${alleTyper.length === 1 ? 'type' : 'typer'}`}
            >
              <ul className="panel-innst-liste" aria-label="Koblingsnøkkel-typer">
                {crash ? (
                  <li className="panel-innst-kind panel-innst-kind-laast" data-testid="kind-crash">
                    <span className="panel-brikke">
                      <Ikon navn="lock-closed-outline" /> <code>crash</code> — låst
                    </span>
                    <code className="panel-innst-regex">{crash.pattern}</code>
                    {/* DD-43: krasj kobles med koblingsnøkkel, aldri med opptak. */}
                    <span className="panel-tekst-liten">Forhåndsdefinert: krasjverktøyet gir en hendelses-ID, plattformen leser aldri feilen. Kan ikke endres eller fjernes.</span>
                  </li>
                ) : null}
                {kinds.map((k, i) => (
                  <li key={i} className="panel-innst-kind" data-testid={`kind-${i}`}>
                    <label className="panel-felt">
                      <span>Type</span>
                      <input type="text" value={k.kind} maxLength={32} pattern="^[a-z][a-z0-9_]*$" onChange={(e) => sett('correlationKinds', kinds.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} data-testid={`kind-navn-${i}`} />
                    </label>
                    <label className="panel-felt">
                      <span>
                        <Ikon navn="options-outline" /> Mønster verdien må følge
                      </span>
                      <input type="text" value={k.pattern} maxLength={200} onChange={(e) => sett('correlationKinds', kinds.map((x, j) => (j === i ? { ...x, pattern: e.target.value } : x)))} data-testid={`kind-regex-${i}`} />
                    </label>
                    <label className="panel-felt">
                      <span>
                        <Ikon navn="open-outline" /> lenkemal ({'{value}'})
                      </span>
                      <input type="url" value={k.linkTemplate ?? ''} placeholder="ingen lenke" onChange={(e) => sett('correlationKinds', kinds.map((x, j) => (j === i ? { kind: x.kind, pattern: x.pattern, ...(e.target.value ? { linkTemplate: e.target.value } : {}) } : x)))} data-testid={`kind-lenke-${i}`} />
                    </label>
                    {/* Ikke `fare`: dette er en UTKASTendring som angres ved å la være å lagre (DD-53). */}
                    <Knapp label="Fjern" onClick={() => sett('correlationKinds', kinds.filter((_, j) => j !== i))} testid={`kind-fjern-${i}`} />
                  </li>
                ))}
              </ul>
              <Knapp label="Ny type" grunn={kinds.length >= 20 ? 'Høyst 20 typer' : null} onClick={() => sett('correlationKinds', [...kinds, { kind: '', pattern: '' }])} testid="kind-ny" />
            </Rad>

            {/*
              Kundeoppslaget (M4.10, «Kundeoppslag for kundeservice» i tech-decisions.md): én lenkemal
              med «{sub}», ingen bryter — satt er på, tomt felt er av (PATCH med null). Tjeneren setter
              pseudonymet inn på saken; plattformen følger aldri lenken. Verdilinjen er vertsnavnet,
              ikke hele adressen. Regelen står som grunn når den brytes — aldri som hjelpetekst.
            */}
            <Rad
              id="kundeoppslag"
              navn="Kundeoppslag"
              kode="customerLookupUrlTemplate"
              handling="Endre"
              endret={endret('kundeoppslag')}
              verdi={
                kundeMal.trim() ? (
                  <>
                    <strong>På</strong> · {kundeoppslagVert(kundeMal.trim())}
                  </>
                ) : (
                  'Av'
                )
              }
              verdiSmal={kundeMal.trim() ? 'På' : 'Av'}
            >
              <label className="panel-felt panel-innst-bred">
                <span>
                  <Ikon navn="open-outline" /> Lenkemal ({'{sub}'} er pseudonymet)
                </span>
                <input
                  type="url"
                  value={kundeMal}
                  maxLength={KUNDEOPPSLAG_MAKS}
                  placeholder="https://… ?sub={sub}"
                  onChange={(e) => sett('customerLookupUrlTemplate', e.target.value.trim() ? e.target.value : null)}
                  onBlur={() => setKundeBeroert(true)}
                  aria-invalid={kundeBeroert && kundeGrunn ? true : undefined}
                  aria-describedby={kundeGrunn ? 'kundeoppslag-grunn' : undefined}
                  data-testid="kundeoppslag-mal"
                />
                {kundeGrunn ? (
                  <span id="kundeoppslag-grunn" className="panel-grunn" role="status" data-testid="kundeoppslag-grunn">
                    <Ikon navn="close-circle-outline" /> {kundeGrunn}
                  </span>
                ) : null}
              </label>
              <p className="panel-tekst-liten">Saken får «Åpne kunden», med pseudonymet der {'{sub}'} står. Tomt felt slår det av.</p>
            </Rad>
          </Seksjon>

          <Seksjon id="svar" tittel="Svar">
            <Rad
              id="svarfrist"
              navn="Svarfrist"
              kode="reviewDeadlineDays"
              handling="Endre"
              endret={endret('svarfrist')}
              verdi={
                <>
                  <strong>{frist} dager</strong> i Vurderes
                </>
              }
              verdiSmal={`${frist} dager`}
            >
              {/* Taket (1–90, kan kortes, aldri forlenges) håndheves av feltet og av backend. */}
              <label className="panel-felt">
                <span>Svarfrist i Vurderes (dager)</span>
                <input type="number" min={1} max={FRIST_TAK} value={frist} onChange={(e) => sett('reviewDeadlineDays', Number(e.target.value))} data-testid="frist" />
              </label>
            </Rad>

            <Rad
              id="statusnavn"
              navn="Statusnavn"
              kode="submissionStatus (tema)"
              handling="Vis"
              verdi={
                <>
                  Feil: {ORDEN_FEIL.map((st) => statusEtikett(st, 'feil')).join(' · ')} — Ønske: {ORDEN_ONSKE.map((st) => statusEtikett(st, 'onske')).join(' · ')}
                </>
              }
              verdiSmal={`Feil: ${ORDEN_FEIL.length} · Ønske: ${ORDEN_ONSKE.length}`}
            >
              {/* Visning i v1 — ikke et kontraktfelt, og derfor «Vis», ikke «Endre». */}
              <p className="panel-tekst-liten">Dette ser innsenderen. Kan ikke endres ennå.</p>
              <table className="panel-tabell">
                <thead>
                  <tr>
                    <th scope="col">Kategori</th>
                    <th scope="col">Status</th>
                    <th scope="col">Ved feil</th>
                    <th scope="col">Ved ønske</th>
                  </tr>
                </thead>
                <tbody>
                  {KATEGORIER.map((kat) => {
                    const statuser = ORDEN_FEIL.filter((st) => statusOppforing(st).statusType === kat);
                    return (
                      <tr key={kat}>
                        <td>{KATEGORI[kat]}</td>
                        <td>{side.tekniske ? <code>{statuser.join(', ')}</code> : <span className="panel-tekst-liten">{statuser.length}</span>}</td>
                        <td>{statuser.map((st) => statusEtikett(st, 'feil')).join(', ')}</td>
                        <td>{statuser.map((st) => statusEtikett(st, 'onske')).join(', ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Rad>

            <Rad
              id="svarmaler"
              navn="Svarmaler"
              kode="replyTemplates"
              handling="Endre"
              endret={endret('svarmaler')}
              verdi={
                <>
                  <strong>
                    {maler.length} {maler.length === 1 ? 'mal' : 'maler'}
                  </strong>
                  {maler.length ? ` · ${maler.filter((m) => m.reasonCode).length} for avslag` : ''}
                </>
              }
              verdiSmal={`${maler.length} ${maler.length === 1 ? 'mal' : 'maler'}`}
            >
              <p className="panel-tekst-liten">Utgangspunkt for svaret. Maks 280 tegn.</p>
              <ul className="panel-innst-liste" aria-label="Svarmaler">
                {maler.map((m, i) => (
                  <li key={i} className="panel-innst-mal" data-testid={`mal-${i}`}>
                    <label className="panel-felt">
                      <span>Navn</span>
                      <input type="text" value={m.name} maxLength={60} onChange={(e) => sett('replyTemplates', maler.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`mal-navn-${i}`} />
                    </label>
                    <label className="panel-felt">
                      <span>Avvisningskode (valgfri)</span>
                      <select value={m.reasonCode ?? ''} onChange={(e) => sett('replyTemplates', maler.map((x, j) => (j === i ? { name: x.name, text: x.text, ...(e.target.value ? { reasonCode: e.target.value as ReasonCode } : {}) } : x)))} data-testid={`mal-kode-${i}`}>
                        <option value="">— uten avslag</option>
                        {AVVISNINGSKODER.map(([kode, navn]) => (
                          <option key={kode} value={kode}>
                            {navn}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="panel-felt panel-innst-bred">
                      <span>Tekst ({m.text.length}/280)</span>
                      <textarea value={m.text} rows={2} maxLength={280} onChange={(e) => sett('replyTemplates', maler.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} data-testid={`mal-tekst-${i}`} />
                    </label>
                    <Knapp label="Fjern" onClick={() => sett('replyTemplates', maler.filter((_, j) => j !== i))} testid={`mal-fjern-${i}`} />
                  </li>
                ))}
              </ul>
              <Knapp label="Ny mal" grunn={maler.length >= 20 ? 'Høyst 20 maler' : null} onClick={() => sett('replyTemplates', [...maler, { name: '', text: '' }])} testid="mal-ny" />
            </Rad>
          </Seksjon>

          <Seksjon id="betaprogram" tittel="Betaprogram">
            <Rad
              id="kontakter"
              navn="Maks kontakter"
              kode="maxContactsPerProgram"
              handling="Endre"
              endret={endret('kontakter')}
              verdi={
                <>
                  <strong>{verdi('maxContactsPerProgram')} per program</strong>
                </>
              }
            >
              <label className="panel-felt">
                <span>Maks kontakter per program</span>
                <input type="number" min={0} max={KONTAKTER_TAK} value={verdi('maxContactsPerProgram')} onChange={(e) => sett('maxContactsPerProgram', Number(e.target.value))} data-testid="kontakter" />
              </label>
            </Rad>
            {/* «Programmenes form» er ingen innstilling: det er formen alle programmene tvinges
                til av K18. Raden finnes bare når den er låst, for at låsen skal være synlig. */}
            {minors ? <Rad id="programform" navn="Programmenes form" handling={null} laast verdi={<><strong>Forpliktelsesfritt</strong> · ingen belønning, frist eller vilkår</>} verdiSmal="Forpliktelsesfritt" /> : null}
            <div className="panel-innst-om">
              {/* DD-14: taket vises ÉN gang, her — aldri som hjelpetekst på feltet. */}
              <Om tittel="kontakttaket" testid="om-kontakttaket">
                <p>
                  Hard grense {KONTAKTER_TAK} — kan senkes, aldri heves. Plattformen nekter å sende en fjerde uansett hva programmet er satt til.
                </p>
                <p>Testeren ser tallet før hun sier ja.</p>
                {/* DD-25: stillhetsvarselet er en fast regel, ingen innstilling. */}
                <p>Én planlagt oppfølging per uke per program, pluss én rett etter en handling. Et program uten hendelse på {STILLHET_UKER * 7} dager merkes i innboksen; fristen røres ikke av det.</p>
              </Om>
            </div>
          </Seksjon>

          <ProdukterOgNoekler erEier={erEier} onMelding={(tekst) => setToast({ tekst })} />

          <Seksjon id="community" tittel="Community">
            <Rad
              id="community"
              navn="Community og stemming"
              kode="communityEnabled · communityChecklist"
              handling="Endre"
              laast={minors}
              endret={endret('community')}
              verdi={
                minors ? (
                  'Av'
                ) : (
                  <>
                    {verdi('communityEnabled') ? 'På' : 'Av'} ·{' '}
                    <strong>
                      {oppfylt} av {s.communityChecklist.length} krav oppfylt
                    </strong>
                  </>
                )
              }
              verdiSmal={minors ? 'Av' : `${verdi('communityEnabled') ? 'På' : 'Av'} · ${oppfylt} av ${s.communityChecklist.length}`}
            >
              {/* DD-18: sjekklisten står på stedet, og «ikke oppfylt» sies ÉN gang — i summen. */}
              <ol className="panel-innst-krav" aria-label="Community-sjekklisten">
                {s.communityChecklist.map((k) => (
                  <li key={k.key} data-testid={`krav-${k.key}`}>
                    <Ikon navn={k.ok ? 'checkmark-circle-outline' : 'ellipse-outline'} className={k.ok ? 'panel-innst-ok' : undefined} />
                    <span>
                      <span className={k.ok ? undefined : 'panel-innst-krav-mangler'}>{k.title}</span>
                      <span className="panel-tekst-liten">{k.detail}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <p className="panel-tekst-liten">{mangler ? `${mangler} av ${s.communityChecklist.length} krav er ikke oppfylt.` : 'Alle krav er oppfylt.'}</p>
              <Bryter label="Community og stemming" ikon="ellipse-outline" paa={verdi('communityEnabled') && !communityLaas} grunn={communityLaas} onChange={(v) => sett('communityEnabled', v)} testid="community" />
            </Rad>
          </Seksjon>

          {/* ÉN linje for alle K18-låsene, og bare når de virker. For behandler og leser står
              linjen uten peker til Organisasjon: siden finnes ikke for dem. */}
          {minors ? (
            <div className="panel-innst-om" data-testid="om-laasene">
              <Om tittel="låsene" testid="om-laasene-lag">
                <p>Så lenge brukergruppen kan være mindreårig, står tekstgrensen på 140 tegn, skjermbilde er av, programmene er forpliktelsesfrie, og community er av.</p>
                <p>{erEier ? 'Bryteren står under Organisasjon → Leietakeren.' : 'En eier kan endre dette.'}</p>
              </Om>
            </div>
          ) : null}
        </RadProvider>
      </div>

      {erEier ? <Fot antall={endringer} endretNavn={endretNavn} lagrer={oppdater.isPending} feil={feil} sperret={sperret} onLagre={lagre} onForkast={tøm} onLukkFeil={() => setFeil(null)} /> : null}

      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}
